import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { promptTemplateService } from '../../../services/promptTemplateService';

const PromptTemplateManager = ({ useCaseId, aiToolId, useCaseName, aiToolName, onRefresh }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [templateText, setTemplateText] = useState('');
  const [templateId, setTemplateId] = useState(null);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const template = await promptTemplateService?.getPromptTemplate(useCaseId, aiToolId);
      if (template) {
        setTemplateText(template?.templateText);
        setTemplateId(template?.id);
      } else {
        setTemplateText('');
        setTemplateId(null);
      }
    } catch (err) {
      console.error('Error loading template:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    loadTemplate();
  };

  const handleClose = () => {
    setIsOpen(false);
    setTemplateText('');
    setTemplateId(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await promptTemplateService?.upsertPromptTemplate({
        useCaseId,
        aiToolId,
        templateText
      });
      onRefresh?.();
      handleClose();
    } catch (err) {
      console.error('Error saving template:', err);
      alert('Failed to save template: ' + (err?.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!templateId) return;
    if (!confirm('Are you sure you want to delete this template?')) return;
    
    try {
      setSaving(true);
      await promptTemplateService?.deletePromptTemplate(templateId);
      onRefresh?.();
      handleClose();
    } catch (err) {
      console.error('Error deleting template:', err);
      alert('Failed to delete template: ' + (err?.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const insertPlaceholder = (placeholder) => {
    const textarea = document.getElementById('template-textarea');
    if (!textarea) return;

    const start = textarea?.selectionStart;
    const end = textarea?.selectionEnd;
    const text = templateText;
    const before = text?.substring(0, start);
    const after = text?.substring(end);
    const newText = before + placeholder + after;
    
    setTemplateText(newText);
    
    // Set cursor position after inserted placeholder
    setTimeout(() => {
      textarea?.focus();
      textarea?.setSelectionRange(start + placeholder?.length, start + placeholder?.length);
    }, 0);
  };

  const placeholders = [
    { label: 'Subject Description', value: '[Subject Description]' },
    { label: 'Action / Pose', value: '[Action / Pose]' },
    { label: 'Environment / Background', value: '[Environment / Background]' },
    { label: 'Art Style / Medium', value: '[Art Style / Medium]' },
    { label: 'Lighting / Mood', value: '[Lighting / Mood]' },
    { label: 'Camera Angle / Composition', value: '[Camera Angle / Composition]' },
    { label: 'Quality / Details', value: '[Quality / Details]' }
  ];

  if (!isOpen) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={handleOpen}
        iconName="FileText"
        iconPosition="left"
      >
        Prompt Template
      </Button>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-xl font-semibold text-foreground">Prompt Template</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {useCaseName} → {aiToolName}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="X" size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Info */}
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-foreground">
                    <p className="font-medium mb-1">Create a template with placeholders</p>
                    <p className="text-muted-foreground">
                      Use placeholders like [Subject Description], [Action / Pose], etc. When generating prompts, these will be replaced with user input data.
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick Insert Placeholders */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Quick Insert Placeholders
                </label>
                <div className="flex flex-wrap gap-2">
                  {placeholders?.map((ph) => (
                    <button
                      key={ph?.value}
                      onClick={() => insertPlaceholder(ph?.value)}
                      className="px-3 py-1.5 text-xs bg-muted hover:bg-muted/80 text-foreground rounded-md border border-border transition-colors"
                    >
                      {ph?.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Template Text */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Template Text
                </label>
                <textarea
                  id="template-textarea"
                  value={templateText}
                  onChange={(e) => setTemplateText(e?.target?.value)}
                  placeholder="A [Subject Description],\n\n[Action / Pose],\n\nin a [Environment / Background],\n\nillustrated in a [Art Style / Medium],\n\nwith,\n\n[Lighting / Mood],\n\n[Camera Angle / Composition],\n\n[Quality / Details]"
                  className="w-full h-64 px-3 py-2 bg-background border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-foreground font-mono text-sm resize-none"
                />
              </div>

              {/* Example */}
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-xs font-medium text-foreground mb-2">Example Template:</p>
                <pre className="text-xs text-muted-foreground whitespace-pre-wrap font-mono">
{`A [Subject Description],

[Action / Pose],

in a [Environment / Background],

illustrated in a [Art Style / Medium],

with,

[Lighting / Mood],

[Camera Angle / Composition],

[Quality / Details]`}
                </pre>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border flex items-center justify-between">
          <div>
            {templateId && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDelete}
                disabled={saving}
                iconName="Trash2"
                iconPosition="left"
              >
                Delete Template
              </Button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleSave}
              disabled={!templateText?.trim() || saving}
              loading={saving}
              iconName="Save"
              iconPosition="left"
            >
              Save Template
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptTemplateManager;